import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CircleBan } from "@/types/circle";
import { toast } from "sonner";

export function useAdminCircleBans() {
  const queryClient = useQueryClient();

  const { data: bans, isLoading, error } = useQuery({
    queryKey: ["admin-circle-bans"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("circle_bans")
        .select(`
          *,
          user:profiles!circle_bans_user_id_fkey(id, first_name, last_name, email, avatar_url)
        `)
        .is("unbanned_at", null)
        .order("banned_at", { ascending: false });

      if (error) throw error;
      return data as CircleBan[];
    },
  });

  const banUser = useMutation({
    mutationFn: async ({ userId, reason }: { userId: string; reason?: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("circle_bans")
        .upsert({
          user_id: userId,
          banned_at: new Date().toISOString(),
          banned_by: user.id,
          reason,
          auto_ban: false,
          unbanned_at: null,
          unbanned_by: null,
        }, {
          onConflict: "user_id",
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-circle-bans"] });
      toast.success("User banned from Circle");
    },
    onError: (error) => {
      console.error("Failed to ban user:", error);
      toast.error("Failed to ban user");
    },
  });

  const unbanUser = useMutation({
    mutationFn: async (userId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("circle_bans")
        .update({
          unbanned_at: new Date().toISOString(),
          unbanned_by: user.id,
        })
        .eq("user_id", userId)
        .is("unbanned_at", null);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-circle-bans"] });
      toast.success("User unbanned from Circle");
    },
    onError: (error) => {
      console.error("Failed to unban user:", error);
      toast.error("Failed to unban user");
    },
  });

  return {
    bans: bans || [],
    isLoading,
    error,
    banUser,
    unbanUser,
  };
}
